<template>
  <v-card
    v-if="!isFullscreen && selectedRevision"
    variant="flat"
    tile
    :min-height="48"
    :max-height="48"
    class="d-flex flex-row border border-b-0 rounded-t-lg rounded-b-0"
    style="transition: all 225ms cubic-bezier(0.4, 0, 0.2, 1)"
  >
    <div class="flex-grow-1 align-self-center ms-3">
      <span class="text-h6 font-weight-bold"> {{ instruction.form.title }} Instructions </span>
    </div>

    <div class="align-self-center me-2">
      <StatusChip
        :color="selectedRevision?.isDraft() ? 'high-emphasis' : 'success'"
        variant="outlined"
        :text="`${selectedRevision?.isDraft() ? 'Draft' : 'Published'} - View`"
        :append-icon="revisions.length > 1 ? 'mdi-menu-down' : ''"
        ref="statusMenuRef"
        style="width: fit-content"
      >
      </StatusChip>
      <v-menu
        v-if="revisions.length > 1"
        :activator="statusMenuRef"
        :target="statusMenuRef"
        offset-y
      >
        <v-list density="comfortable" slim nav class="rounded">
          <v-list-item
            v-for="revision in revisions"
            :key="revision.id!"
            :title="revision.revisionToString()"
            :color="revision.isDraft() ? 'high-emphasis' : 'success'"
            :active="selectedRevision?.id === revision.id"
            slim
            @click="setSelectedRevision(revision)"
          >
            <template #prepend>
              <v-icon
                size="18"
                :icon="revision.isDraft() ? 'mdi-pencil' : 'mdi-check'"
                class="me-1"
              />
            </template>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <div
      v-if="
        selectedRevision.isDraft() &&
        currentUser?.hasPermission('post_orders.change_postorderinstructions')
      "
      class="d-flex align-self-center"
    >
      <AlTooltipButton
        text="Publish Post Order Instruction"
        color="success"
        variant="flat"
        position="bottom"
        size="auto"
        class="me-2 px-3"
        :loading="isPublishing"
        style="font-size: 0.875rem; height: 32px"
        @click="publishInstructionRevision()"
      >
        Publish
      </AlTooltipButton>
    </div>

    <slot
      name="edit-header-action"
      v-bind="{ latestInstructionRevision: selectedRevision, setSelectedRevision }"
    ></slot>
  </v-card>

  <v-layout
    full-height
    class="flex-fill section-layout"
    :class="{ 'section-layout--fullscreen': isFullscreen }"
  >
    <PostOrderInstructionDetailSectionList
      :post-order-id="postOrderId"
      :post-order-instruction-id="postOrderInstructionId!"
      :revision-id="selectedRevision?.id!"
      :sections="selectedRevision?.sections ?? []"
      :selected-section="selectedSection?.id"
      :instruction-selected="showInstructionDescription"
      :section-error="error"
      @instruction-pressed="sectionInstruction()"
      @edit-section-pressed="openPostOrderInstructionSectionFormDialog"
      @select-section-pressed="(section) => selectSection(section.id!)"
      @create-post-order-instruction-section-pressed="openPostOrderInstructionSectionFormDialog()"
    />

    <PostOrderInstructionDetailSectionFormDialog
      v-model:dialog="isPostOrderInstructionSectionFormDialogActive"
      :post-order-instruction-section="formSelectedSectionData"
      :isEdit="isEditingPostOrderInstructionSection"
      @saved-section="(section) => selectSection(section.id!)"
    />

    <v-main
      flat
      id="sections-container"
      class="rounded-lg bg-background border-t border-b"
      style="overflow-y: scroll"
    >
      <v-card
        class="d-flex flex-column"
        tile
        variant="flat"
        height="100%"
        :class="isFieldDetailDrawerActive ? 'mr-n4' : ''"
      >
        <PostOrderInstructionDescriptionCard
          v-if="showInstructionDescription && selectedRevision"
          :instruction="instruction"
          :revision="selectedRevision"
        />

        <NoDataAvailablePlaceholder
          class="pt-6"
          v-else-if="selectedRevision && selectedRevision.sections.length == 0"
          width="400"
          header="No checkpoints added"
          header-class="font-weight-medium text-h5"
        >
          <template #description>
            <p class="mt-0 mb-6 text-caption text-medium-emphasis">
              <span> You do not have any sections for this post order instruction.</span>
            </p>
          </template>

          <template #primaryAction>
            <v-btn color="primary" depressed @click="openPostOrderInstructionSectionFormDialog()">
              <template #prepend>
                <v-icon left class="hidden-sm-and-down"> mdi-plus </v-icon>
              </template>
              Create Checkpoint
            </v-btn>
          </template>
        </NoDataAvailablePlaceholder>

        <template v-else-if="selectedSection">
          <div>
            <v-toolbar density="compact" color="transparent" class="border-b">
              <template #title>
                <span class="text-subtitle-1 font-weight-medium">{{
                  selectedSection.section.title
                }}</span>
              </template>

              <template #append>
                <Transition :name="transition">
                  <div v-if="sectionHasPhotoOrDescription">
                    <v-tooltip
                      :text="`${isSectionDescriptionPreview ? 'Shrink' : 'Expand'} checkpoint description preview`"
                    >
                      <template #activator="{ props }">
                        <v-btn
                          v-bind="props"
                          @click="toggleSectionDescriptionPreview()"
                          color="medium-emphasis"
                          variant="text"
                          class="rounded-circle mr-1"
                          density="comfortable"
                          size="x-small"
                          :icon="
                            isSectionDescriptionPreview ? 'mdi-chevron-down' : 'mdi-chevron-up'
                          "
                        ></v-btn>
                      </template>
                    </v-tooltip>
                  </div>
                </Transition>
              </template>
            </v-toolbar>
            <v-expand-transition>
              <v-card
                v-if="sectionHasPhotoOrDescription && isSectionDescriptionPreview"
                variant="flat"
                color="#fafafa"
                tile
                class="rounded-0 d-flex flex-row border-b"
                style="transition: all 225ms cubic-bezier(0.4, 0, 0.2, 1) !important"
              >
                <v-card-text
                  class="d-flex justify-start align-start mask-bottom-card-small text-medium-emphasis pt-3 pb-5 pe-8 ps-5"
                >
                  {{ selectedSection.section.description }}
                </v-card-text>

                <ImageViewer
                  v-if="selectedSection.photo"
                  :src="selectedSection.photo as string"
                  class="align-end pa-3"
                  height="100%"
                  :width="184"
                  contain
                />
              </v-card>
            </v-expand-transition>
          </div>
          <PostOrderInstructionDetailSectionFieldCanvas
            ref="sectionCanvasRef"
            :is-section-vehicle-log="
              selectedSection.type == PostOrderInstructionSectionTypeEnum.VehicleLog
            "
          />
        </template>

        <template v-else-if="!selectedSection">
          <NoDataAvailablePlaceholder
            class="pt-16"
            width="400"
            header="No section selected"
            header-class="font-weight-medium text-h5"
          >
            <template #description>
              <p class="mt-0 mb-6 text-caption text-medium-emphasis">
                <span> Select a section from the left panel to view fields workflow </span>
              </p>
            </template>
          </NoDataAvailablePlaceholder>
        </template>
      </v-card>
    </v-main>

    <PostOrderInstructionDetailFieldDrawer
      v-if="selectedNode"
      v-model="isFieldDetailDrawerActive"
      :selectedNode="selectedNode"
    />
  </v-layout>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, provide, ref, watch } from 'vue'
import { useAuthStore } from '@/stores'
import { storeToRefs } from 'pinia'

import { type ISystemError } from '@/models/error'
import type { IPostOrderNode } from '@/models/post-order/tree'
import { useSetPostOrderInstructionRevisionState } from '@/composables/post-order'

import {
  PostOrderInstructionRevision,
  PostOrderInstructionRevisionStateEnum,
  PostOrderInstructionSectionTypeEnum,
  type IPostOrderInstruction,
  type IPostOrderInstructionRevision,
  type IPostOrderInstructionsSection,
  type IPostOrderInstructionsSectionData
} from '@/models/post-order'

import {
  PostOrderCanvasSymbol,
  PostOrderFullscreenSymbol,
  PostOrderNodeSymbol,
  PostOrderSymbol
} from '@/components/post-order/postOrderProvide'

import NoDataAvailablePlaceholder from '@/components/common/NoDataAvailablePlaceholder.vue'
import PostOrderInstructionDetailFieldDrawer from '@/components/post-order/PostOrderInstructionDetailFieldDrawer.vue'
import PostOrderInstructionDetailSectionFieldCanvas from '@/components/post-order/PostOrderInstructionDetailSectionFieldCanvas.vue'
import PostOrderInstructionDetailSectionFormDialog from './PostOrderInstructionDetailSectionFormDialog.vue'
import PostOrderInstructionDetailSectionList from '@/components/post-order/PostOrderInstructionDetailSectionList.vue'
import PostOrderInstructionDescriptionCard from '@/components/post-order/PostOrderInstructionDescriptionCard.vue'
import ImageViewer from '../common/ImageViewer.vue'
import AlTooltipButton from '../common/AlTooltipButton.vue'

interface Props {
  revisions: IPostOrderInstructionRevision[]
  instruction: IPostOrderInstruction
  postOrderId: number
}

const props = defineProps<Props>()

// TODO MG 2025-05-11: move set instruction state feature to this component to remove unnecessary error model
const error = defineModel<ISystemError | null>('error', {
  default: null
})

const authStore = useAuthStore()
const { getUser: currentUser } = storeToRefs(authStore)

const postOrderId = computed(() => props.postOrderId)
const postOrderInstructionId = computed(() => props.instruction.id!)

const selectedSection = ref<IPostOrderInstructionsSection | null>(null)
const postOrderInstructionSectionId = computed(() => selectedSection.value?.id!)

const selectedRevision = ref<IPostOrderInstructionRevision | null>(null)

function setSelectedRevision(revision: IPostOrderInstructionRevision) {
  selectedRevision.value = revision
}

const latestInstructionRevision = computed(() => {
  // Prioritize published revision first, fallback to draft if none found
  const publishedRevision = props.revisions.find(
    (revision) => revision.state === PostOrderInstructionRevisionStateEnum.Published
  )
  if (publishedRevision) return publishedRevision

  return (
    props.revisions.find(
      (revision) => revision.state === PostOrderInstructionRevisionStateEnum.Draft
    ) ?? null
  )
})

/*
 * Watch for changes in revisions and set the currently selected revision to the latest one
 * This is to ensure that if the revisions change, we always have the latest one selected.
 * Ensure we don't override the selection if it was already set.
 */
watch(
  () => props.revisions,
  (revisions) => {
    const currentRevision =
      revisions.find((revision) => revision.id === selectedRevision.value?.id) ??
      latestInstructionRevision.value

    selectedRevision.value = currentRevision
  },
  { immediate: true, deep: true }
)

const postOrderInstructionRevisionId = computed(() => selectedRevision.value?.id!)

const statusMenuRef = ref<HTMLElement>()
const sectionCanvasRef = ref<typeof PostOrderInstructionDetailSectionFieldCanvas>()

provide(PostOrderCanvasSymbol, {
  disabled: computed(() => selectedRevision.value?.isPublished() ?? false)
})

provide(PostOrderSymbol, {
  postOrderId: postOrderId,
  instructionId: postOrderInstructionId,
  sectionId: postOrderInstructionSectionId,
  revisionId: postOrderInstructionRevisionId
})

const showInstructionDescription = ref(false)

function sectionInstruction() {
  showInstructionDescription.value = true
  sectionCanvasRef.value?.resetViewPort()
  selectedSection.value = null
}

function selectSection(sectionId: number) {
  showInstructionDescription.value = false
  const foundSection = selectedRevision.value
    ? selectedRevision.value.sections.find(
        (postOrderSection) => postOrderSection.id == sectionId
      ) ?? null
    : null
  selectedSection.value = foundSection

  // pan to center
  sectionCanvasRef.value?.resetViewPort()
}

watch(selectedSection, () => {
  // Deselect any selected node
  selectNode()
})

watch(
  selectedRevision,
  (value) => {
    if (value && value.sections.length > 0) {
      if (selectedSection.value) {
        const isSelectedSectionInNewSectionList = value.sections.find(
          (section) => section.id == selectedSection.value!.id
        )

        if (!isSelectedSectionInNewSectionList) {
          const firstSection = value.sections[0]
          // Select first section if no sections currently selected
          selectSection(firstSection.id!)
        }
      } else {
        if (showInstructionDescription.value) return
        // If no section was selected then just select the first section in the list
        const firstSection = value.sections[0]

        if (firstSection) selectSection(firstSection.id!)
      }
    }
  },
  { deep: true }
)

onMounted(() => {
  // Show instruction description by default
  showInstructionDescription.value = true
})
// TREE NODE
const selectedNode = ref<IPostOrderNode | null>(null)

function selectNode(node?: IPostOrderNode) {
  selectedNode.value = node ?? null

  if (node) {
    isFieldDetailDrawerActive.value = true
  }
}

provide(PostOrderNodeSymbol, {
  selectedNode,
  selectNode
})

function clearErrors() {
  error.value = null
}

const isFieldDetailDrawerActive = ref(false)

const isPostOrderInstructionSectionFormDialogActive = ref(false)
const formSelectedSectionData = ref<IPostOrderInstructionsSectionData | null>(null)
const isEditingPostOrderInstructionSection = ref(false)

function openPostOrderInstructionSectionFormDialog(section?: IPostOrderInstructionsSection) {
  isEditingPostOrderInstructionSection.value = section ? true : false

  formSelectedSectionData.value = section
    ? section
    : {
        type: PostOrderInstructionSectionTypeEnum.Checkpoint,
        section: {
          order: selectedRevision.value ? selectedRevision.value.sections.length + 1 : 1
        }
      }

  isPostOrderInstructionSectionFormDialogActive.value = true
}

const isReverseTransition = ref(false)

const transition = computed(() => {
  return !isReverseTransition.value ? 'scroll-y-transition' : 'scroll-y-reverse-transition'
})

watch(selectedSection, (value) => {
  const isReversed = !(value && !!value.photo && !!value.section.description)
  isReverseTransition.value = isReversed
})

const isSectionDescriptionPreview = ref(true)

function toggleSectionDescriptionPreview() {
  isSectionDescriptionPreview.value = !isSectionDescriptionPreview.value
}

// watcher in fullscreen to hide description when true

const postOrderFullscreenContext = inject(PostOrderFullscreenSymbol)
if (!postOrderFullscreenContext)
  throw new Error('[Post Order] Could not find injected post order context')

// Fullscreen
const isFullscreen = computed(() => {
  return !!postOrderFullscreenContext.isFullscreen.value
})

watch(isFullscreen, (value) => {
  if (value) {
    isSectionDescriptionPreview.value = false
  }
})

const sectionHasPhotoOrDescription = computed(
  () => !!(selectedSection.value?.photo || selectedSection.value?.section.description)
)

const { mutate: publishMutation, isPending: isPublishPending } =
  useSetPostOrderInstructionRevisionState()

const isPublishing = computed(() => isPublishPending.value)

function publishInstructionRevision() {
  clearErrors()

  publishMutation(
    {
      postOrderId: postOrderId.value,
      instructionId: postOrderInstructionId.value,
      revisionId: selectedRevision.value!.id!,
      state: PostOrderInstructionRevisionStateEnum.Published
    },
    {
      onSuccess: (data) => {
        // re-select revision
        const revision = new PostOrderInstructionRevision(data)
        setSelectedRevision(revision)
      },
      onError: (err) => {
        error.value = err
      }
    }
  )
}
</script>

<style lang="scss">
.section-layout {
  height: calc(-406px + 100vh);

  &--fullscreen {
    height: 100vh !important;
    max-height: 100vh;
  }
}
</style>
